import type { FlipPad, PulseChart } from '@shared/chart';
import type { EngineState } from '@shared/engine/pulse/state';
import { ARC_DURATION_MS, RAIL_THICKNESS, TRAINING_WHEELS_HIDE_AFTER_HITS } from './pulseRenderConstants';

/**
 * Owns the player cube rendering + its arc-tween state. The engine flips
 * gravity instantly on the input frame; we re-render an interpolated
 * position for ARC_DURATION_MS so the cube visually arcs to the new
 * surface. Pure cosmetic — timing windows + scroll math remain
 * frame-accurate.
 *
 * Also draws the ghost cube (dotted destination preview) for first-run
 * onboarding. Lives in the same class because both depend on the same
 * training-wheels gate and surface bookkeeping.
 */
export class PlayerRenderer {
  private prevSurfaceForArc: 'floor' | 'ceiling' | null = null;
  private arcFromSurface: 'floor' | 'ceiling' | null = null;
  private arcStartedAtMs = 0;

  drawGhostCube(
    ctx: CanvasRenderingContext2D,
    chart: PulseChart,
    state: EngineState,
    beat: number,
    x: number,
    ceilingY: number,
    floorY: number,
    size: number,
    flipAnim: number,
  ): void {
    if (!state.alive) {
      return;
    }
    if (state.perfects + state.goods >= TRAINING_WHEELS_HIDE_AFTER_HITS) {
      return;
    }
    if (flipAnim >= 0.4) {
      return;
    }
    let nextPad: FlipPad | null = null;
    for (const pad of chart.pads) {
      if (pad.optional) {
        continue;
      }
      if (state.hitPadIds.has(pad.id)) {
        continue;
      }
      if (pad.beat < beat - 0.05) {
        continue;
      }
      if (pad.beat > beat + 3) {
        continue;
      }
      if (nextPad === null || pad.beat < nextPad.beat) {
        nextPad = pad;
      }
    }
    if (!nextPad) {
      return;
    }

    const destSurface: 'floor' | 'ceiling' =
      state.currentSurface === 'floor' ? 'ceiling' : 'floor';
    const ghostY = destSurface === 'floor' ? floorY - size : ceilingY + RAIL_THICKNESS;
    const realY =
      state.currentSurface === 'floor' ? floorY - size : ceilingY + RAIL_THICKNESS;
    const distance = nextPad.beat - beat;
    // Squared curve so the ghost is barely visible until the pad is close,
    // then snaps into focus in the last beat.
    const t = Math.max(0, Math.min(1, 1 - distance / 2.5));
    const eased = t * t;
    const alpha = 0.15 + eased * 0.55;

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = chart.palette.pad;
    ctx.shadowColor = chart.palette.pad;
    ctx.shadowBlur = 6 + eased * 14;
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 3]);
    ctx.strokeRect(x - size / 2, ghostY, size, size);

    const liveCenterY = realY + size / 2;
    const ghostCenterY = ghostY + size / 2;
    const lineStartY = Math.min(liveCenterY, ghostCenterY) + size / 2;
    const lineEndY = Math.max(liveCenterY, ghostCenterY) - size / 2;
    ctx.beginPath();
    ctx.moveTo(x, lineStartY);
    ctx.lineTo(x, lineEndY);
    ctx.stroke();
    ctx.restore();
  }

  drawPlayer(
    ctx: CanvasRenderingContext2D,
    state: EngineState,
    x: number,
    ceilingY: number,
    floorY: number,
    size: number,
    flipAnim: number,
  ): void {
    ctx.save();
    const surfaceY = (s: 'floor' | 'ceiling'): number =>
      s === 'floor' ? floorY - size : ceilingY + RAIL_THICKNESS;
    const targetY = surfaceY(state.currentSurface);

    const nowMs = performance.now();
    if (this.prevSurfaceForArc !== state.currentSurface) {
      if (this.prevSurfaceForArc !== null) {
        this.arcFromSurface = this.prevSurfaceForArc;
        this.arcStartedAtMs = nowMs;
      }
      this.prevSurfaceForArc = state.currentSurface;
    }

    let baseY = targetY;
    if (this.arcFromSurface !== null) {
      const elapsed = nowMs - this.arcStartedAtMs;
      const t = Math.min(1, Math.max(0, elapsed / ARC_DURATION_MS));
      if (t >= 1) {
        this.arcFromSurface = null;
      } else {
        const fromY = surfaceY(this.arcFromSurface);
        const eased = 1 - Math.pow(1 - t, 5);
        baseY = fromY + (targetY - fromY) * eased;
      }
    }
    const squashX = 1 + flipAnim * 0.35;
    const squashY = 1 - flipAnim * 0.35;
    const drawW = size * squashX;
    const drawH = size * squashY;
    const offsetY = state.currentSurface === 'floor' ? size - drawH : 0;

    const drawX = x - drawW / 2;
    const drawY = baseY + offsetY;

    ctx.shadowColor = '#ff2bd6';
    ctx.shadowBlur = 24 + flipAnim * 14;
    ctx.fillStyle = state.alive ? '#ff2bd6' : '#3a0a18';
    ctx.fillRect(drawX, drawY, drawW, drawH);
    ctx.shadowBlur = 0;
    ctx.fillStyle = state.alive ? '#fff' : '#1a0a14';
    const innerInset = Math.max(2, drawW * 0.18);
    ctx.fillRect(
      drawX + innerInset,
      drawY + innerInset,
      drawW - innerInset * 2,
      drawH - innerInset * 2,
    );

    if (state.alive) {
      ctx.fillStyle = `rgba(255,255,255,${0.15 + flipAnim * 0.5})`;
      const trailLen = 26 + flipAnim * 30;
      // Trail extends to the LEFT of the cube. Pads scroll right→left past
      // the player, which from the cube's frame is equivalent to the cube
      // moving rightward through the world — the motion trail therefore
      // sits behind on the left.
      ctx.fillRect(drawX - trailLen, drawY + drawH * 0.3, trailLen, drawH * 0.4);
    }

    if (
      state.alive &&
      state.perfects + state.goods < TRAINING_WHEELS_HIDE_AFTER_HITS &&
      flipAnim < 0.4
    ) {
      const pointsUp = state.currentSurface === 'floor';
      const arrowHalf = drawW * 0.22;
      const cx = drawX + drawW / 2;
      const cy = drawY + drawH / 2;
      ctx.save();
      ctx.fillStyle = '#ff2bd6';
      ctx.globalAlpha = 0.85;
      ctx.shadowColor = '#ff2bd6';
      ctx.shadowBlur = 4;
      ctx.beginPath();
      if (pointsUp) {
        ctx.moveTo(cx, cy - arrowHalf);
        ctx.lineTo(cx - arrowHalf, cy + arrowHalf * 0.6);
        ctx.lineTo(cx + arrowHalf, cy + arrowHalf * 0.6);
      } else {
        ctx.moveTo(cx, cy + arrowHalf);
        ctx.lineTo(cx - arrowHalf, cy - arrowHalf * 0.6);
        ctx.lineTo(cx + arrowHalf, cy - arrowHalf * 0.6);
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    ctx.restore();
  }
}
